import { getFirstCaseInsensitiveValue, normalizeBoolean } from './appsheetFieldUtils';

const splitCsv = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value)
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
};

const buildAddress = (row) => {
  const line1 = getFirstCaseInsensitiveValue(row, [
    'Address',
    'Street',
    'Street Address',
    'Street_Address',
    'Billing Street',
    'BillingStreet',
    'Billing_Street',
    'Address_Line_1',
    'Address_Line1',
    'Address1',
    'Address 1',
  ]);
  const line2 = getFirstCaseInsensitiveValue(row, [
    'Address Line 2',
    'Address_Line_2',
    'Address_Line2',
    'Address2',
    'Address 2',
  ]);
  const city = getFirstCaseInsensitiveValue(row, ['City', 'Billing City', 'BillingCity', 'Billing_City']);
  const state = getFirstCaseInsensitiveValue(row, ['State', 'Province', 'Billing State', 'BillingState', 'Billing_State']);
  const zip = getFirstCaseInsensitiveValue(row, [
    'Zip',
    'ZIP',
    'Postal Code',
    'Postal_Code',
    'Billing Postal Code',
    'BillingPostalCode',
    'Billing_Postal_Code',
  ]);
  const parts = [line1, line2, city, state, zip].filter((part) => part && String(part).trim());
  if (!parts.length) return '';
  return parts.join(', ');
};

export const buildAccountModel = (row = {}) => {
  const accountId = getFirstCaseInsensitiveValue(row, [
    'Account_ID__c',
    'AccountId',
    'accountId',
    'account_id',
    'Id',
    'id',
  ]);
  const name = getFirstCaseInsensitiveValue(row, [
    'Name',
    'name',
    'Account Name',
    'Account_Name',
    'Account_Name__c',
  ]);
  const bac = getFirstCaseInsensitiveValue(row, [
    'BAC_Code__c',
    'BAC_Code__C',
    'bac_code__c',
    'bac_code__C',
    'BAC',
    'bac',
  ]);
  const status = getFirstCaseInsensitiveValue(row, [
    'Account_Status__c',
    'Account Status',
    'Account_Status',
    'account_status__c',
    'accountStatus',
    'Status',
  ]);
  const brands = splitCsv(
    getFirstCaseInsensitiveValue(row, [
      'SF_Brands__c',
      'SF_Brands__C',
      'SF Brands',
      'SF_Brands',
      'Brands',
      'Brand',
      'Brand__c',
    ])
  );
  const ccid = getFirstCaseInsensitiveValue(row, [
    'CCID__c',
    'Customer_CCID__c',
    'Customer_CCID',
    'Customer CCID',
    'CCID',
    'Ccid',
  ]);
  const position = getFirstCaseInsensitiveValue(row, [
    'Account Position',
    'Account_Position',
    'Account_Position__c',
    'AccountPosition',
    'Position',
  ]);
  const inProduction = normalizeBoolean(
    getFirstCaseInsensitiveValue(row, [
      'In Production',
      'In_Production',
      'In_Production__c',
      'IsProduction',
      'Is_Production',
      'Production',
      'Production__c',
    ])
  );
  const websiteEnrollment = normalizeBoolean(
    getFirstCaseInsensitiveValue(row, [
      'Website Enrollment',
      'Website_Enrollment',
      'Website_Enrollment__c',
      'Website Enrolled',
      'Website_Enrolled__c',
      'Web Enrollment',
      'Web_Enrollment',
      'Web_Enrollment__c',
      'WebsiteEnrollment',
      'Enrollment',
      'Enrolled',
    ])
  );
  const websiteUrl = getFirstCaseInsensitiveValue(row, [
    'Website',
    'Website URL',
    'Website_URL',
    'Website__c',
    'WebsiteUrl',
  ]);

  return {
    id: accountId ? String(accountId).trim() : '',
    name: name ? String(name).trim() : '',
    bac: bac ? String(bac).trim() : '',
    status: status ? String(status).trim() : '',
    brands,
    ccid: ccid ? String(ccid).trim() : '',
    position: position ? String(position).trim() : '',
    inProduction,
    websiteEnrollment,
    websiteUrl: websiteUrl ? String(websiteUrl).trim() : '',
    address: buildAddress(row),
    raw: row,
  };
};

const summarizeUnique = (values) => {
  const normalized = values.map((value) => String(value ?? '').trim()).filter(Boolean);
  if (!normalized.length) return '';
  const unique = Array.from(new Set(normalized));
  return unique.length === 1 ? unique[0] : '';
};

const summarizeBoolean = (values) => {
  const normalized = values.filter((value) => value !== null);
  if (!normalized.length) return null;
  const unique = Array.from(new Set(normalized));
  if (unique.length === 1) return unique[0];
  return null;
};

export const buildAccountSummary = (accounts = [], bac = '') => {
  const names = accounts.map((account) => account.name).filter(Boolean);
  const statuses = accounts.map((account) => account.status).filter(Boolean);
  const ccids = accounts.map((account) => account.ccid).filter(Boolean);
  const positions = accounts.map((account) => account.position).filter(Boolean);
  const productionValues = accounts.map((account) => account.inProduction).filter((v) => v !== null);
  const enrollmentValues = accounts.map((account) => account.websiteEnrollment).filter((v) => v !== null);
  const allBrands = accounts.flatMap((account) => account.brands || []);

  return {
    name: names.length ? `All accounts (${accounts.length})` : 'All accounts',
    bac: summarizeUnique(accounts.map((account) => account.bac)) || bac,
    status: summarizeUnique(statuses),
    ccid: summarizeUnique(ccids),
    position: summarizeUnique(positions),
    inProduction: summarizeBoolean(productionValues),
    websiteEnrollment: summarizeBoolean(enrollmentValues),
    brands: Array.from(new Set(allBrands)),
  };
};
